'use client';

import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { DefiEcosystemData, UseDefiLlamaDataReturn } from '@/types/defi';
import type { ChainId } from '@/lib/chains';

/**
 * Hook for fetching DeFi ecosystem data from Firestore
 * Real-time listener on `defi_ecosystem/<chain>` document.
 *
 * - No argument (or `undefined`) → falls back to `'current'`, the Abstract alias
 *   kept for back-compat so all existing callers are unaffected.
 * - Pass a `ChainId` string (e.g. `'base'`) to listen to the per-chain doc
 *   `defi_ecosystem/base` written by the persistence agent.
 *
 * @param chain - Optional ChainId; defaults to `'current'` (Abstract alias)
 * @returns DeFi ecosystem data with loading/error states
 */
export function useDefiLlamaData(chain?: ChainId): UseDefiLlamaDataReturn {
  const [data, setData] = useState<DefiEcosystemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastDataRef = useRef<string | null>(null);

  // Calculate if data is stale (>2 hours old)
  const isStale = (() => {
    if (!data?.lastSuccessfulFetch) return false;
    const fetchTime = new Date(data.lastSuccessfulFetch).getTime();
    const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
    return fetchTime < twoHoursAgo;
  })();

  useEffect(() => {
    // Skip during SSR or if Firebase is not available
    if (!db || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Resolve the Firestore doc id:
      //   - no chain arg → 'current' (the Abstract back-compat alias)
      //   - chain arg    → the per-chain doc written by the persistence agent
      //                    e.g. 'base' → defi_ecosystem/base
      const docId = chain ?? 'current';
      const docRef = doc(db, 'defi_ecosystem', docId);

      const unsubscribe = onSnapshot(
        docRef,
        (snapshot) => {
          try {
            if (snapshot.exists()) {
              const ecosystemData = snapshot.data() as DefiEcosystemData;
              
              // Check if data actually changed before processing
              const dataString = JSON.stringify(ecosystemData);
              if (dataString === lastDataRef.current) {
                return; // Skip if unchanged
              }
              
              // Clear existing debounce timer
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }
              
              // Debounce updates (100ms)
              debounceTimerRef.current = setTimeout(() => {
                lastDataRef.current = dataString;
                setData(ecosystemData);
                setLastUpdated(new Date());
                setError(null);
                setLoading(false);
              }, 100);
              
            } else {
              setData(null);
              setError('No DeFi ecosystem data available');
              setLoading(false);
            }
          } catch (err) {
            console.error('Error processing DeFi data:', err);
            setError('Failed to process DeFi data');
            setLoading(false);
          }
        },
        (err) => {
          console.warn('Firestore DeFi listener error:', err.message);
          setError(`Failed to fetch DeFi data: ${err.message}`);
          setLoading(false);
        }
      );

      // Cleanup on unmount
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        unsubscribe();
      };
    } catch (err) {
      console.warn('Failed to initialize DeFi data listener:', err);
      setError('Failed to initialize DeFi data listener');
      setLoading(false);
    }
  // Re-subscribe whenever the target chain changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    isStale
  };
}

/**
 * Format large numbers for display
 */
export function formatDefiNumber(value: number, style: 'compact' | 'full' | 'currency' = 'compact'): string {
  if (!value && value !== 0) return '-';
  
  if (style === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  if (style === 'full') {
    return new Intl.NumberFormat('en-US').format(value);
  }
  
  // Compact format
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(2)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
}

/**
 * Format percentage change
 * Uses 1 decimal for values >= 10% or <= -10%, otherwise 2 decimals
 */
export function formatPercentChange(value: number): string {
  if (!value && value !== 0) return '-';
  const prefix = value >= 0 ? '+' : '';
  const decimals = Math.abs(value) >= 10 ? 1 : 2;
  return `${prefix}${value.toFixed(decimals)}%`;
}

/**
 * Get time ago string
 */
export function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default useDefiLlamaData;

